import React, { useState, useEffect, useMemo, useRef } from 'react';
import Section from './OnlineExamSections';
import 'react-quill/dist/quill.snow.css';

const parseOfflineFlag = value => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
    }
    return false;
};

const ensureOfflineFlag = sectionsArray =>
    Array.isArray(sectionsArray)
        ? sectionsArray.map(section => ({
              ...section,
              offline: parseOfflineFlag(section?.offline),
          }))
        : [];

// Normalization helper
const normalizeSections = (data) => {
    if (Array.isArray(data)) {
        // Check if it's already in sections format (has timeLimit and passages)
        if (data.length > 0 && data[0].timeLimit !== undefined && Array.isArray(data[0].passages)) {
            return ensureOfflineFlag(data); // Already in sections format
        }
        // Legacy: array of passages, wrap in one section
        return [{
            id: Date.now(),
            timeLimit: 30,
            passages: data,
            offline: false
        }];
    } else if (data && Array.isArray(data.sections)) {
        // New: already in sections format
        return ensureOfflineFlag(data.sections);
    } else {
        return [];
    }
};

// Deep compare helper
function deepEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
}

const TestBuilder = ({ sections: sectionsProp, setSections: setSectionsProp }) => { 
    // Use useMemo to avoid re-normalizing unless prop changes
    const normalizedSections = useMemo(() => normalizeSections(sectionsProp), [sectionsProp]);
    const [sections, setSections] = useState(normalizedSections);
    const [showSectionId, setShowSectionId] = useState(null); // Only one open at a time
    const [selectedPassages, setSelectedPassages] = useState({});
    const [userHasMadeChanges, setUserHasMadeChanges] = useState(false);
    const [onlineExam, setOnlineExam] = useState(true);

    // Sync sections state with normalizedSections only when it changes
    useEffect(() => {
        setSections(normalizedSections);
        if (normalizedSections.length > 0) {
            const firstSectionOffline = parseOfflineFlag(normalizedSections[0]?.offline);
            setOnlineExam(!firstSectionOffline);
        } else {
            setOnlineExam(true);
        }
        setUserHasMadeChanges(false); // Reset flag when prop changes
    }, [normalizedSections]);

    // Only initialize selectedPassages when sections actually change
    useEffect(() => {
        const initialSelections = {};
        sections.forEach(section => {
            if (Array.isArray(section.passages) && section.passages.length > 0) {
                initialSelections[section.id] = 0; // Select first passage if available
            }
        });
        setSelectedPassages(prev => {
            // Only reset if the section ids have changed
            const prevIds = Object.keys(prev);
            const newIds = sections.map(s => s.id.toString());
            if (prevIds.length !== newIds.length || !prevIds.every(id => newIds.includes(id))) {
                return initialSelections;
            }
            return prev;
        });
    }, [sections]);

    // Only update showSectionId when a section is added or the open section is deleted
    const prevSectionsRef = useRef(sections);
    useEffect(() => {
        // If a section was added, open the new one
        if (sections.length > prevSectionsRef.current.length) {
            setShowSectionId(sections[sections.length - 1].id);
        }
        // If the currently open section was deleted, open the first one (if any)
        else if (!sections.some(s => s.id === showSectionId) && sections.length > 0) {
            setShowSectionId(sections[0].id);
        }
        prevSectionsRef.current = sections;
    }, [sections, showSectionId]);

    const addSection = () => {
        const newSection = { id: Date.now(), timeLimit: 30, calculatorEnabled: false, passages: [], offline: !onlineExam };
        setSections([...sections, newSection]);
        setShowSectionId(newSection.id); // Show the new section
        setSelectedPassages(prev => ({ ...prev, [newSection.id]: 0 }));
        setUserHasMadeChanges(true); // Mark that user made changes
    };

    const updateSection = (index, newSection) => {
        setSections(sections.map((s, i) => (i === index ? newSection : s)));
        setUserHasMadeChanges(true); // Mark that user made changes
        // If passages array length changed, update selectedPassages only if needed
        const sectionId = sections[index].id;
        if (newSection.passages.length === 0) {
            setSelectedPassages(prev => ({ ...prev, [sectionId]: 0 }));
        } else if (
            selectedPassages[sectionId] >= newSection.passages.length &&
            newSection.passages.length > 0
        ) {
            setSelectedPassages(prev => ({ ...prev, [sectionId]: newSection.passages.length - 1 }));
        }
    };

    const removeSection = (index, sectionId) => {
        setSections(sections.filter((_, i) => i !== index));
        if (showSectionId === sectionId) setShowSectionId(null);
        setSelectedPassages(prev => {
            const updated = { ...prev };
            delete updated[sectionId];
            return updated;
        });
        setUserHasMadeChanges(true); // Mark that user made changes
    };

    // Accordion-style: only one section open at a time
    const toggleSectionVisibility = (sectionId) => {
        setShowSectionId(prevId => (prevId === sectionId ? null : sectionId));
        setSelectedPassages(prevState => ({
            ...prevState,
            [sectionId]: 0
        }));
    };

    // Add passage to a section and update selected passage index
    const addPassageToSection = (sectionIndex) => {
        const sectionId = sections[sectionIndex].id;
        const newPassage = { id: Date.now(), passage: '', questions: [] };
        const newSections = sections.map((s, i) =>
            i === sectionIndex
                ? { ...s, passages: [...s.passages, newPassage] }
                : s
        );
        setSections(newSections);
        // Set the selected passage to the newly added passage (last in array)
        setSelectedPassages(prev => ({
            ...prev,
            [sectionId]: newSections[sectionIndex].passages.length - 1
        }));
        setUserHasMadeChanges(true); // Mark that user made changes
    };

    const handleOfflineToggle = (event) => {
        const offline = event.target.checked;
        setOnlineExam(!offline);
        setSections(prevSections =>
            prevSections.map(section => ({
                ...section,
                offline
            }))
        );
        setUserHasMadeChanges(true);
    };

    // Only call setSectionsProp when user has made changes
    const lastSent = useRef();
    useEffect(() => {
        if (setSectionsProp && userHasMadeChanges && !deepEqual(lastSent.current, sections)) {
            setSectionsProp(sections); // Send just the sections array, not wrapped in object
            lastSent.current = sections;
        }
    }, [sections, setSectionsProp, userHasMadeChanges]);


    return (
        <div>
            <h1>Online Test Builder</h1>

            <div className="form-check form-switch mb-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="offlineExamToggle"
                    checked={!onlineExam}
                    onChange={handleOfflineToggle}
                />
                <label className="form-check-label" htmlFor="offlineExamToggle">
                    Offline Exam
                </label>
            </div>

            {sections.map((section, index) => (
                <div key={section.id} className="mb-3 border p-3">
                    <button 
                        className='btn btn-info mb-2'
                        onClick={() => toggleSectionVisibility(section.id)}
                    >
                        {showSectionId === section.id ? "Hide Section" : "Show Section"}
                    </button>

                    {showSectionId === section.id && (
                        <Section
                            section={section}
                            index={index}
                            updateSection={updateSection}
                            removeSection={() => removeSection(index, section.id)}
                            selectedPassageIndex={selectedPassages[section.id]}
                            setSelectedPassageIndex={value =>
                                setSelectedPassages(prev => ({
                                    ...prev,
                                    [section.id]: value
                                }))
                            }
                            onAddPassage={addPassageToSection}
                        />
                    )}
                </div>
            ))}

            <button className='btn btn-secondary' onClick={addSection}>Add Section</button>
        </div>
    );
};

export default TestBuilder;
